// src/catalog/launch/CatalogLaunchShell.jsx

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCatalogLaunch } from "./CatalogLaunchContext";
import CatalogLaunchAboutModal from "./CatalogLaunchAboutModal";
import CatalogLaunchRuntimeLaunchConfigurationControl from "./CatalogLaunchRuntimeLaunchConfigurationControl";
import {
  getEntryShortDescription,
  getEntryTitle,
  humanizeValue
} from "../utils/catalogDisplay";

function getFallbackTitle(viewDefinition, viewId, t) {
  const fallback =
    viewDefinition?.label ||
    humanizeValue(viewId) ||
    "Catalog launch";

  if (viewDefinition?.labelKey && typeof t === "function") {
    return t(viewDefinition.labelKey, {
      defaultValue: fallback
    });
  }

  return fallback;
}

function CatalogLaunchShell({
  titleOverride,
  subtitleOverride,
  backLabelOverride,
  backToOverride,
  onBack,
  showBack = true,
  showHeader = true,
  children,
  className = "",
  contentClassName = "",
  headerActions = null,
  contextControls = null
}) {
  const { t } = useTranslation();
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const {
    catalogEntry,
    viewDefinition,
    viewId,
    backLabel,
    backTo,
    selectedLaunchConfigurationId
  } = useCatalogLaunch();

  const derivedTitle = catalogEntry
    ? getEntryTitle(catalogEntry, t)
    : getFallbackTitle(viewDefinition, viewId, t);

  const derivedSubtitle = catalogEntry
    ? getEntryShortDescription(catalogEntry, t)
    : "";

  const title =
    titleOverride !== undefined
      ? titleOverride
      : derivedTitle;

  const subtitle =
    subtitleOverride !== undefined
      ? subtitleOverride
      : derivedSubtitle;

  const resolvedBackLabel =
    backLabelOverride !== undefined
      ? backLabelOverride
      : backLabel || "Back to catalog";
  const resolvedBackTo =
    backToOverride !== undefined
      ? backToOverride
      : backTo || "/app/catalog";

  const shellClassName = [
    "app-module",
    "catalog-launch-shell",
    className
  ].filter(Boolean).join(" ");

  const contentClassNameValue = [
    "catalog-launch-shell__content",
    contentClassName
  ].filter(Boolean).join(" ");

  const shouldRenderTitleBlock =
    title || subtitle || headerActions || catalogEntry;
  const shouldRenderHeader =
    showHeader && (
      shouldRenderTitleBlock ||
      contextControls ||
      selectedLaunchConfigurationId
    );
  const shouldRenderResourceHeader =
    showBack || shouldRenderHeader;
  const runtimeLaunchConfigurationControl =
    selectedLaunchConfigurationId
      ? <CatalogLaunchRuntimeLaunchConfigurationControl />
      : null;
  const hasContextControls =
    Boolean(runtimeLaunchConfigurationControl || contextControls);

  const renderBackControl = () => {
    if (!showBack) {
      return null;
    }

    if (onBack) {
      return (
        <button
          type="button"
          className="catalog-launch-shell__back"
          onClick={onBack}
        >
          {resolvedBackLabel}
        </button>
      );
    }

    if (!resolvedBackTo) {
      return null;
    }

    return (
      <Link
        className="catalog-launch-shell__back"
        to={resolvedBackTo}
      >
        {resolvedBackLabel}
      </Link>
    );
  };

  return (
    <div className={shellClassName}>
      {shouldRenderResourceHeader && (
        <header className="catalog-launch-shell__resource-header">
          {showBack && (
            <nav
              className="catalog-launch-shell__nav"
              aria-label="Catalog launch navigation"
            >
              {renderBackControl()}
            </nav>
          )}

          {shouldRenderHeader && (
            <div className="catalog-launch-shell__header">
              {shouldRenderTitleBlock && (
                <div className="catalog-launch-shell__title-row">
                  <div className="catalog-launch-shell__header-main">
                    {title && (
                      <h1 className="catalog-launch-shell__title">
                        {title}
                      </h1>
                    )}
                    {subtitle && (
                      <p className="catalog-launch-shell__subtitle">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  {headerActions && (
                    <div className="catalog-launch-shell__header-actions">
                      {headerActions}
                    </div>
                  )}
                  {catalogEntry && (
                    <button
                      type="button"
                      className="catalog-launch-shell__about-button"
                      onClick={() => setIsAboutOpen(true)}
                    >
                      About this resource
                    </button>
                  )}
                </div>
              )}

              {hasContextControls && (
                <div className="catalog-launch-shell__context-controls">
                  {runtimeLaunchConfigurationControl}
                  {contextControls}
                </div>
              )}
            </div>
          )}
        </header>
      )}

      <div className={contentClassNameValue}>
        {children}
      </div>

      <CatalogLaunchAboutModal
        entry={catalogEntry}
        selectedLaunchConfigurationId={selectedLaunchConfigurationId}
        isOpen={isAboutOpen}
        onRequestClose={() => setIsAboutOpen(false)}
      />
    </div>
  );
}

export default CatalogLaunchShell;
